// frontend/src/components/dashboard/Dashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'https://api.lamilara.com/api';
const MESES = ['Ene','Feb','Mar','Abr','May','Jun',
               'Jul','Ago','Sep','Oct','Nov','Dic'];

function fmt(val) {
  const n = parseFloat(val || 0);
  return `$${n.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
}

// Genera los 3 años más recientes dinámicamente
function getAnios() {
  const anioActual = new Date().getFullYear();
  return [anioActual, anioActual - 1, anioActual - 2];
}

// ── KPI Card ──────────────────────────────────────────────
function KpiCard({ titulo, valor, sub, color, icono }) {
  return (
    <div style={{
      background:'#fff', borderRadius:14, padding:'18px 22px',
      boxShadow:'0 2px 8px rgba(0,0,0,.07)', borderLeft:`5px solid ${color}`,
      flex:1, minWidth:150,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <span style={{ fontSize:11, color:'#6b7280', fontWeight:600,
                       textTransform:'uppercase', letterSpacing:'.5px' }}>
          {titulo}
        </span>
        <span style={{ fontSize:20 }}>{icono}</span>
      </div>
      <div style={{ fontSize:24, fontWeight:800, color, marginTop:8, letterSpacing:'-0.5px' }}>
        {valor}
      </div>
      {sub && <div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Tooltip del gráfico ───────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#1a3a5c', borderRadius:10,
                  padding:'10px 14px', fontSize:12, color:'#fff' }}>
      <p style={{ fontWeight:700, color:'#93c5fd', marginBottom:4 }}>{MESES[(label||1)-1]}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ margin:'2px 0', color: p.color }}>
          {p.name}: <strong>{fmt(p.value)}</strong>
        </p>
      ))}
    </div>
  );
}

// ── Tabla deudores ────────────────────────────────────────
function TablaDeudores({ deudores }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, padding:'20px 24px',
                  boxShadow:'0 2px 8px rgba(0,0,0,.07)', flex:1 }}>
      <h3 style={{ fontSize:12, fontWeight:700, color:'#1a3a5c', marginBottom:14,
                   textTransform:'uppercase', letterSpacing:'.5px' }}>
        ⚠️ Principales Deudores
      </h3>
      {deudores.length === 0 ? (
        <div style={{ textAlign:'center', padding:'20px 0', color:'#15803d' }}>
          <div style={{ fontSize:32 }}>🎉</div>
          <div style={{ fontSize:13, marginTop:8 }}>Sin deudores pendientes</div>
        </div>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:'2px solid #f3f4f6' }}>
              {['Quinta','Zona','Saldo'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'6px 8px',
                                     color:'#6b7280', fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deudores.map((d, i) => (
              <tr key={d.casa_id} style={{
                background: i%2===0 ? '#fafafa' : '#fff',
                borderBottom:'1px solid #f3f4f6'
              }}>
                <td style={{ padding:'8px', fontWeight:600, color:'#1a3a5c' }}>
                  {d.nombre_quinta}
                </td>
                <td style={{ padding:'8px', color:'#6b7280' }}>{d.zona}</td>
                <td style={{ padding:'8px', fontWeight:700, color:'#dc2626' }}>
                  {fmt(d.saldo_pendiente)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── DASHBOARD PRINCIPAL ───────────────────────────────────
export default function Dashboard() {
  const anios = getAnios();
  const [anio,     setAnio]    = useState(anios[0]);
  const [flujo,    setFlujo]   = useState(null);
  const [deudores, setDeudores]= useState([]);
  const [saldoIni, setSaldoIni]= useState(0);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [dashRes, casasRes, configRes] = await Promise.all([
        fetch(`${API}/casas/dashboard/${anio}`,{headers:{Authorization:'Bearer '+(localStorage.getItem('calleb_token')||'')}}).then(r=>r.json()),
        fetch(`${API}/casas?estatus=MOROSO&limite=8`,{headers:{Authorization:'Bearer '+(localStorage.getItem('calleb_token')||'')}}).then(r=>r.json()),
        fetch(`${API}/config/saldo-inicial`,{headers:{Authorization:'Bearer '+(localStorage.getItem('calleb_token')||'')}}).then(r=>r.json()),
      ]);
      if (dashRes.ok)   setFlujo(dashRes);
      if (casasRes.ok)  setDeudores(casasRes.casas || []);
      if (configRes.ok) setSaldoIni(parseFloat(configRes.valor) || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [anio]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center',
                  alignItems:'center', height:400 }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
        <div style={{ fontSize:14, color:'#6b7280' }}>Cargando dashboard…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding:24 }}>
      <div style={{ padding:16, background:'#fef2f2', color:'#dc2626',
                    borderRadius:12, fontSize:13 }}>⚠️ {error}</div>
    </div>
  );

  const { kpis = {}, meses = [] } = flujo || {};
  const totalIngresos = parseFloat(kpis.total_ingresos || 0);
  const totalEgresos  = parseFloat(kpis.total_egresos  || 0);
  const saldoNeto     = totalIngresos - totalEgresos;
  const saldoCaja     = saldoIni + saldoNeto;
  const tasaMorosidad = kpis.total_casas > 0
    ? ((kpis.morosos / kpis.total_casas) * 100).toFixed(1) : 0;
  const deudaTotal    = parseFloat(kpis.deuda_total || 0);

  // Línea acumulada: saldo_inicial + ingresos acumulados - egresos acumulados mes a mes
  let acumulado = saldoIni;
  const datosLinea = (meses || []).map((m, i) => {
    acumulado += (m.ingresos - m.egresos);
    return {
      mes: i + 1,
      nombre: MESES[i],
      Ingresos: m.ingresos,
      Egresos:  m.egresos,
      'Saldo Acumulado': parseFloat(acumulado.toFixed(2)),
    };
  });

  // Dona morosidad
  const dataDona = [
    { name: 'Solventes', value: parseInt(kpis.solventes || 0) },
    { name: 'Morosas',   value: parseInt(kpis.morosos   || 0) },
  ];
  const DONA_COLORES = ['#15803d', '#dc2626'];

  return (
    <div style={{ padding:24, background:'#f0f4f8', minHeight:'100vh' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:'#1a3a5c', margin:0 }}>
            Dashboard
          </h2>
          <p style={{ fontSize:12, color:'#6b7280', margin:'4px 0 0' }}>
            Resumen financiero del condominio
          </p>
        </div>
        <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}
                style={{ padding:'8px 14px', borderRadius:8,
                         border:'1px solid #d1d5db', fontSize:13,
                         background:'#fff', color:'#374151' }}>
          {anios.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* ── Saldo disponible en caja — cuadro simple ── */}
      <div style={{
        background:'linear-gradient(135deg, #1a3a5c 0%, #1e4976 100%)',
        borderRadius:14, padding:'28px 32px', marginBottom:20,
        boxShadow:'0 4px 16px rgba(26,58,92,.3)',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:16,
      }}>
        <div>
          <div style={{ fontSize:12, color:'#93c5fd', fontWeight:600,
                        textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>
            💰 Saldo Disponible en Caja
          </div>
          <div style={{ fontSize:44, fontWeight:900, color:'#fff', letterSpacing:'-2px' }}>
            {fmt(saldoCaja)}
          </div>
        </div>
        <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#93c5fd', marginBottom:4 }}>Saldo Inicial</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#fff' }}>{fmt(saldoIni)}</div>
          </div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#93c5fd', marginBottom:4 }}>+ Cobrado {anio}</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#4ade80' }}>{fmt(totalIngresos)}</div>
          </div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#93c5fd', marginBottom:4 }}>− Gastos {anio}</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#f87171' }}>{fmt(totalEgresos)}</div>
          </div>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:20 }}>
        <KpiCard titulo="Total Cobrado" valor={fmt(totalIngresos)}
                 sub={`Año ${anio}`} color="#15803d" icono="💵"/>
        <KpiCard titulo="Total Gastos" valor={fmt(totalEgresos)}
                 sub="Egresos del período" color="#dc2626" icono="📤"/>
        <KpiCard titulo="Saldo Neto" valor={fmt(saldoNeto)}
                 sub="Cobrado − Gastos"
                 color={saldoNeto >= 0 ? '#1a3a5c' : '#dc2626'} icono="📊"/>
        <KpiCard titulo="Casas Morosas" valor={`${kpis.morosos||0} de ${kpis.total_casas||0}`}
                 sub={`Tasa: ${tasaMorosidad}%`} color="#f59e0b" icono="⚠️"/>
        <KpiCard titulo="Deuda Total" valor={fmt(deudaTotal)}
                 sub="Saldo por cobrar" color="#7c3aed" icono="📋"/>
      </div>

      {/* ── Gráficos fila ── */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr',
                    gap:16, marginBottom:16 }}>

        {/* Línea: Saldo acumulado en el tiempo */}
        <div style={{ background:'#fff', borderRadius:14, padding:'20px 24px',
                      boxShadow:'0 2px 8px rgba(0,0,0,.07)' }}>
          <h3 style={{ fontSize:12, fontWeight:700, color:'#1a3a5c', marginBottom:4,
                       textTransform:'uppercase', letterSpacing:'.5px' }}>
            Evolución del Saldo en Caja — {anio}
          </h3>
          <p style={{ fontSize:11, color:'#9ca3af', marginBottom:14, margin:'0 0 14px' }}>
            Saldo inicial + ingresos − gastos acumulados mes a mes
          </p>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={datosLinea} margin={{ top:5, right:10, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="nombre" tick={{ fontSize:10, fill:'#9ca3af' }}
                     axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} axisLine={false}
                     tickLine={false} tickFormatter={v => `$${v}`}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Line type="monotone" dataKey="Saldo Acumulado" stroke="#1a3a5c"
                    strokeWidth={3} dot={{ r:4, fill:'#1a3a5c', strokeWidth:2, stroke:'#fff' }}
                    activeDot={{ r:6 }}/>
              <Line type="monotone" dataKey="Ingresos" stroke="#15803d"
                    strokeWidth={1.5} strokeDasharray="4 4"
                    dot={false}/>
              <Line type="monotone" dataKey="Egresos" stroke="#dc2626"
                    strokeWidth={1.5} strokeDasharray="4 4"
                    dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Dona: Estado de casas */}
        <div style={{ background:'#fff', borderRadius:14, padding:'20px 24px',
                      boxShadow:'0 2px 8px rgba(0,0,0,.07)',
                      display:'flex', flexDirection:'column', alignItems:'center',
                      justifyContent:'center' }}>
          <h3 style={{ fontSize:12, fontWeight:700, color:'#1a3a5c', marginBottom:16,
                       textTransform:'uppercase', letterSpacing:'.5px', alignSelf:'flex-start' }}>
            Estado de Casas
          </h3>

          <PieChart width={160} height={160}>
            <Pie data={dataDona} cx={75} cy={75} innerRadius={45}
                 outerRadius={72} paddingAngle={4} dataKey="value"
                 startAngle={90} endAngle={-270}>
              {dataDona.map((_, i) => (
                <Cell key={i} fill={DONA_COLORES[i]}/>
              ))}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} casas`, n]}/>
          </PieChart>

          {/* Leyenda manual */}
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:12, width:'100%' }}>
            {dataDona.map((d, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center',
                                    justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:12, height:12, borderRadius:99,
                                background: DONA_COLORES[i] }}/>
                  <span style={{ fontSize:12, color:'#374151' }}>{d.name}</span>
                </div>
                <span style={{ fontSize:13, fontWeight:700,
                               color: DONA_COLORES[i] }}>{d.value}</span>
              </div>
            ))}
            <div style={{ borderTop:'1px solid #f3f4f6', paddingTop:8, marginTop:4,
                          display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:11, color:'#9ca3af' }}>Morosidad</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#f59e0b' }}>
                {tasaMorosidad}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Deudores ── */}
      <TablaDeudores deudores={deudores}/>
    </div>
  );
}
